import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
} from '@angular/core';
import {
  InfoBannerComponent,
  RadioCardComponent,
  RadioCardGroupComponent,
  TagComponent,
} from '@catch/general-ui';
import { ThemingService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { collapse } from '../../../../assets/animations/animations';
import { StripeBillingInterval } from '../../../enums/stripe.enum';
import { StripeProduct } from '../../../interfaces/stripe';
import { NumberStepperComponent } from '../../number-stepper/number-stepper.component';

export const CUSTOM_AGENTS_OPTION = '__custom__';

@Component({
  selector: 'app-agents-plan-selector',
  imports: [
    CurrencyPipe,
    InfoBannerComponent,
    NgxSkeletonLoaderModule,
    NumberStepperComponent,
    RadioCardComponent,
    RadioCardGroupComponent,
    TagComponent,
    TranslocoPipe,
  ],
  templateUrl: './agents-plan-selector.component.html',
  styleUrl: './agents-plan-selector.component.scss',
  animations: [collapse],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentsPlanSelectorComponent {
  readonly products = input.required<StripeProduct[]>();
  readonly loaded = input.required<boolean>();
  readonly billingInterval = input.required<StripeBillingInterval>();
  readonly disabled = input(false);
  readonly isCustomCurrent = input(false);
  readonly currentPackagePriceId = input<string | null>(null);
  readonly selectedOption = model<string | null>(null);
  readonly customAgentCount = model(1);

  readonly themingService = inject(ThemingService);
  readonly CUSTOM_AGENTS_OPTION = CUSTOM_AGENTS_OPTION;

  readonly singleSeatProduct = computed(
    () =>
      this.products().find((product) => this.maxAgentsOf(product) === 1) ??
      null,
  );

  readonly packageProducts = computed(() =>
    this.products().filter((product) => this.maxAgentsOf(product) !== 1),
  );

  readonly customTotalPrice = computed(
    () =>
      this.monthlyDisplayAmount(this.singleSeatProduct()) *
      this.customAgentCount(),
  );

  readonly showPackageHint = computed(() => this.customAgentCount() >= 9);

  /**
   * Resolves the display title for a catalog price.
   * @param {StripeProduct} product Catalog price
   * @returns {string}
   */
  productTitle(product: StripeProduct): string {
    return product.product?.name ?? product.id;
  }

  /**
   * Returns the monthly display amount in major currency units.
   * @param {StripeProduct | null} product Catalog price
   * @returns {number}
   */
  monthlyDisplayAmount(product: StripeProduct | null): number {
    if (!product) {
      return 0;
    }
    const amount = product.unit_amount / 100;
    return this.billingInterval() === StripeBillingInterval.YEAR
      ? amount / 12
      : amount;
  }

  /**
   * Reads max_agents from price or product metadata.
   * @param {StripeProduct} product Catalog price
   * @returns {number}
   */
  maxAgentsOf(product: StripeProduct): number {
    const directValue = +(product.metadata?.max_agents ?? 0);
    if (directValue > 0) {
      return directValue;
    }
    const nestedProduct = product.product as
      | (StripeProduct['product'] & { metadata?: Record<string, string> })
      | undefined;
    return +(nestedProduct?.metadata?.max_agents ?? 0);
  }

  /**
   * Checks whether a package is currently booked.
   * @param {StripeProduct} product Catalog price
   * @returns {boolean}
   */
  isCurrentPackage(product: StripeProduct): boolean {
    return product.id === this.currentPackagePriceId();
  }
}
